import { i18n } from '@atlas-frontend/i18n';
import { Row, SortingFn } from '@tanstack/react-table';
import {
  FormattedDate,
  FormattedDateTime,
  FormattedTime,
  getIsoDateFromFormattedDate,
  MAX_TIMESTAMP,
} from '../../utils';

export const sortingFns: Record<string, SortingFn<any>> = {
  localeCompare: (a, b, columnId) => localeCompare(a, b, columnId, toLowerCase),
  localeCompareCaseSensitive: (a, b, columnId) =>
    localeCompare(a, b, columnId, passTrough),
  formattedDate: (a, b, columnId) =>
    formattedDateTime(a, b, columnId, formattedDateTimeToDate),
  formattedDateTime: (a, b, columnId) =>
    formattedDateTime(a, b, columnId, formattedDateTimeToDate),
};

const localeCompare = (
  a: Row<any>,
  b: Row<any>,
  columnId: string,
  filterValue: (value: string) => string
) => {
  const aValue = a.renderValue(columnId)?.toString() || '';
  const bValue = b.renderValue(columnId)?.toString() || '';
  return filterValue(aValue).localeCompare(filterValue(bValue), i18n.language);
};

const formattedDateTime = (
  a: Row<any>,
  b: Row<any>,
  columnId: string,
  filterValue: (value: FormattedDate | FormattedDateTime) => Date
) => {
  const aValue = filterValue(
    a.renderValue<FormattedDate | FormattedDateTime>(columnId)
  );
  const bValue = filterValue(
    b.renderValue<FormattedDate | FormattedDateTime>(columnId)
  );
  return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
};

const passTrough = (value: string) => value;
const toLowerCase = (value: string) => value.toLowerCase();

const formattedDateTimeToDate = (
  value: FormattedDate | FormattedDateTime
): Date => {
  if (!value) {
    return new Date(MAX_TIMESTAMP);
  }

  // date + time
  if (value.includes(' ')) {
    const [date, time] = value.split(' ') as [FormattedDate, FormattedTime];
    return new Date(`${getIsoDateFromFormattedDate(date)}T${time}`);
  }

  // time only
  if (value.includes(':')) {
    // 275760-09-12 date is derived from MAX_TIMESTAMP
    return new Date(`275760-09-12T${value}`);
  }

  // date only
  return new Date(`${getIsoDateFromFormattedDate(value as FormattedDate)}`);
};

declare module '@tanstack/react-table' {
  interface SortingFns {
    localeCompare: SortingFn<any>;
    localeCompareCaseSensitive: SortingFn<any>;
    formattedDate: SortingFn<any>;
    formattedDateTime: SortingFn<any>;
  }
}
